/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useContext, useEffect} from 'react';
import {Controller} from 'react-hook-form';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons';
// Components
import {
  Box, Button, FormControl, FormErrorMessage, Input, InputGroup, Select,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {StoreLocatorContext} from '@salesforce/retail-react-app/app/components/store-locator-modal/index';
// Others
import {STORE_LOCATOR_NUM_STORES_PER_LOAD, SUPPORTED_STORE_LOCATOR_COUNTRIES} from '@salesforce/retail-react-app/app/constants';

const useGeolocation = () => {
  const {
    setSearchStoresParams,
    setAutomaticGeolocationHasFailed,
    setUserHasSetManualGeolocation,
    userHasSetManualGeolocation,
  } = useContext(StoreLocatorContext);

  const getGeolocationError = () => {
    setAutomaticGeolocationHasFailed(true);
  };
  const getGeolocationSuccess = (position) => {
    setAutomaticGeolocationHasFailed(false);
    setSearchStoresParams({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      limit: STORE_LOCATOR_NUM_STORES_PER_LOAD,
    });
  };

  const getUserGeolocation = () => {
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(getGeolocationSuccess, getGeolocationError);
      setUserHasSetManualGeolocation(false);
    } else {
      console.log('Geolocation not supported');
    }
  };

  useEffect(() => {
    if (!userHasSetManualGeolocation) getUserGeolocation();
  }, []);

  return getUserGeolocation;
};

const StoreLocatorInput = ({form, submitForm}) => {
  const {
    searchStoresParams,
    userHasSetManualGeolocation,
    automaticGeolocationHasFailed,
    setUserWantsToShareLocation,
    userWantsToShareLocation,
  } = useContext(StoreLocatorContext);

  const getUserGeolocation = useGeolocation();
  const {control} = form;
  const intl = useIntl();

  return (
    <form id="store-locator-form" onSubmit={form.handleSubmit(submitForm)}>
      <InputGroup>
        {SUPPORTED_STORE_LOCATOR_COUNTRIES.length > 0 && (
          <Controller
            control={control}
            name="countryCode"
            defaultValue={
              userHasSetManualGeolocation ? searchStoresParams?.countryCode : ''
            }
            render={({field}) => {
              return SUPPORTED_STORE_LOCATOR_COUNTRIES.length !== 0 ? (
                <FormControl isInvalid={form.formState.errors.countryCode}>
                  <Select
                    {...field}
                    borderColor="gray.500"
                    marginBottom="10px"
                    placeholder={intl.formatMessage({
                      id: 'store_locator.action.select_a_country',
                      defaultMessage: 'Select a country',
                    })}
                  >
                    {SUPPORTED_STORE_LOCATOR_COUNTRIES.map(
                      ({countryCode, countryName}) => {
                        return (
                          <option key={countryCode} value={countryCode}>
                            {intl.formatMessage(countryName)}
                          </option>
                        );
                      }
                    )}
                  </Select>
                  {form.formState.errors.countryCode && (
                    <FormErrorMessage
                      color="red.600"
                      sx={{marginBottom: '10px'}}
                    >
                      <AlertIcon aria-hidden="true" mr={2} />
                      {form.formState.errors.countryCode.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              ) : (
                <></>
              );
            }}
            rules={{
              required: intl.formatMessage({
                id: 'store_locator.error.please_select_a_country',
                defaultMessage: 'Please select a country.',
              }),
            }}
          >
          </Controller>
        )}
      </InputGroup>
      <InputGroup>
        <Controller
          control={control}
          defaultValue={userHasSetManualGeolocation ? searchStoresParams?.postalCode : ''}
          name="postalCode"
          render={({field}) => {
            return (
              <FormControl isInvalid={form.formState.errors.postalCode}>
                <Input
                  {...field}
                  placeholder={intl.formatMessage({
                    id: 'store_locator.field.placeholder.enter_postal_code',
                    defaultMessage: 'Enter postal code',
                  })}
                />
                {form.formState.errors.postalCode && (
                  <FormErrorMessage color="red.600" sx={{top: '-20px'}}>
                    <AlertIcon aria-hidden="true" mr={2} />
                    {form.formState.errors.postalCode.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            );
          }}
          rules={{
            required: intl.formatMessage({
              id: 'store_locator.error.please_enter_a_postal_code',
              defaultMessage: 'Please enter a postal code.',
            }),
          }}
        >
        </Controller>
        <Button
          key="find-button"
          marginLeft={2}
          type="submit"
          variant="solid"
          width="15%"
          onClick={() => {
            setUserWantsToShareLocation(false);
          }}
        >
          {intl.formatMessage({
            id: 'store_locator.action.find',
            defaultMessage: 'Find',
          })}
        </Button>
      </InputGroup>
      <Box
        margin="10px"
        style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
      >
        {intl.formatMessage({
          id: 'store_locator.description.or',
          defaultMessage: 'Or',
        })}
      </Box>
      <Button
        key="use-my-location-button"
        fontWeight="bold"
        marginBottom={4}
        variant="solid"
        width="100%"
        onClick={() => {
          setUserWantsToShareLocation(true);
          getUserGeolocation();
        }}
      >
        {intl.formatMessage({
          id: 'store_locator.action.use_my_location',
          defaultMessage: 'Use My Location',
        })}
      </Button>
      <FormControl isInvalid={automaticGeolocationHasFailed && userWantsToShareLocation}>
        <FormErrorMessage
          alignItems="center"
          color="red.600"
          justifyContent="center"
          marginBottom={4}
        >
          <AlertIcon aria-hidden="true" mr={2} />
          {intl.formatMessage({
            id: 'store_locator.error.agree_to_share_your_location',
            defaultMessage: 'Please agree to share your location',
          })}
        </FormErrorMessage>
      </FormControl>
    </form>
  );
};

StoreLocatorInput.propTypes = {
  form: PropTypes.object,
  submitForm: PropTypes.func,
};

export default StoreLocatorInput;
